/**
 * site2026 全站滚动显现、区块增强与轻量交互绑定
 * 在 layout 路由切换时调用 bindSite2026Motion(pageRoot)
 */

const MOTION_ROOT_MARK = 'data-site2026-motion-root'

let revealObserver = null
const observedEls = new WeakSet()
const cleanups = []

export function bindSite2026Motion(root, options = {}) {
  unbindSite2026Motion()
  if (!root) return { destroy: unbindSite2026Motion }

  if (!options.skipEnhance) {
    enhancePage(root)
  }

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (reduced) {
    showAllReveals(root)
  } else {
    setupRevealObserver(root)
    cleanups.push(setupCodeCardTilt(root))
    tagAiModelLabels(root)
  }

  requestAnimationFrame(() => {
    root.classList.add('is-page-ready')
    if (root.classList.contains('home-page')) {
      root.classList.add('is-ready')
    }
  })

  return { destroy: unbindSite2026Motion }
}

/** 补扫异步挂载的子组件（FeatureGrid 等） */
export function refreshSite2026Motion(root, options = {}) {
  if (!root) return
  if (!options.skipEnhance) {
    enhancePage(root)
  }
  observeNewReveals(root)
}

export function unbindSite2026Motion() {
  if (revealObserver) {
    revealObserver.disconnect()
    revealObserver = null
  }
  cleanups.splice(0).forEach(fn => fn && fn())
}

function showAllReveals(root) {
  root.querySelectorAll('.reveal-block, .reveal-item').forEach(el => {
    el.classList.add('is-visible')
  })
}

function setupRevealObserver(root) {
  if (typeof IntersectionObserver === 'undefined') {
    showAllReveals(root)
    return
  }
  if (!revealObserver) {
    revealObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            revealObserver.unobserve(entry.target)
            observedEls.delete(entry.target)
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    )
  }
  observeNewReveals(root)
}

function observeNewReveals(root) {
  if (!revealObserver) return
  root.querySelectorAll('.reveal-block, .reveal-item').forEach(el => {
    if (observedEls.has(el) || el.classList.contains('is-visible')) return
    observedEls.add(el)
    revealObserver.observe(el)
  })
}

function markReveal(el, type, index) {
  if (el.classList.contains(type)) return
  el.classList.add(type)
  if (index != null) {
    el.style.setProperty('--reveal-i', String(index))
  }
}

function enhancePage(root) {
  if (!root.getAttribute(MOTION_ROOT_MARK)) {
    root.setAttribute(MOTION_ROOT_MARK, '1')
    if (!root.classList.contains('home-page')) {
      root.classList.add('site-page')
    }
  }

  root.querySelectorAll('.section > .container > .center').forEach((el, i) => {
    markReveal(el, 'reveal-block', i % 3)
  })

  root.querySelectorAll('.grid-2 > .card-2026, .grid-3 > .card-2026, .grid-4 > .card-2026').forEach((el, i) => {
    markReveal(el, 'reveal-block', i % 6)
  })

  root.querySelectorAll('.pricing .plan').forEach((el, i) => {
    markReveal(el, 'reveal-block', i)
  })

  root.querySelectorAll('.stats-strip .stat-item').forEach((el, i) => {
    markReveal(el, 'reveal-block', i)
  })

  root.querySelectorAll('.customer-card, .school-card').forEach((el, i) => {
    markReveal(el, 'reveal-block', i % 8)
  })

  root.querySelectorAll('.news-item').forEach((el, i) => {
    markReveal(el, 'reveal-block', i % 6)
  })

  root.querySelectorAll('.prices-table-wrap, .section .code-card').forEach((el, i) => {
    const hasCodeTabs = el.querySelector('.code-langs')
    if (!hasCodeTabs) {
      markReveal(el, 'reveal-block', i % 2)
    }
    if (el.classList.contains('code-card')) {
      el.classList.add('motion-code-card')
    }
  })

  root.querySelectorAll('.ai-writer-text, .ai-writer-pic').forEach((el, i) => {
    markReveal(el, 'reveal-block', i)
  })

  root.querySelectorAll('.ai-page-model-tags').forEach(el => {
    markReveal(el, 'reveal-block', 0)
  })

  root.querySelectorAll('.prose-2026').forEach(el => {
    markReveal(el, 'reveal-block', 0)
  })
}

function tagAiModelLabels(root) {
  root.querySelectorAll('.ai-page-model-tags .ai-hub-tag').forEach((el, i) => {
    el.style.setProperty('--tag-i', String(i % 12))
  })
  return () => {}
}

function setupCodeCardTilt(root) {
  const cards = root.querySelectorAll('.motion-code-card')
  const onEnter = e => e.currentTarget.classList.add('is-tilted')
  const onLeave = e => e.currentTarget.classList.remove('is-tilted')
  cards.forEach(card => {
    card.addEventListener('mouseenter', onEnter)
    card.addEventListener('mouseleave', onLeave)
  })
  return () => {
    cards.forEach(card => {
      card.removeEventListener('mouseenter', onEnter)
      card.removeEventListener('mouseleave', onLeave)
      card.classList.remove('is-tilted')
    })
  }
}
